import { useCallback, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Button, ButtonSize, ButtonType, getButtonSizeFromString } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { webnotesThemeColors, webnotesThemeStyle } from '@nx-frontend/shared/themes/webnotes';
import { PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { $aiModal, closeAiModalEvent } from './store';

interface AiModalProps {
  themeColors?: ThemeColorSchemas;
  themeStyle?: PortalStyleConfig;
}

export function AiModal(props: AiModalProps) {
  const [aiModal, closeAiModal] = useUnit([$aiModal, closeAiModalEvent]);
  const [inAnim, setInAnim] = useState(false);
  const modalWrapperRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const transitionClassNames = {
    appear: 'opacity-0',
    appearActive: 'transition-opacity duration-300 opacity-100',
    enter: 'opacity-0',
    enterDone: 'opacity-100',
    enterActive: 'transition-opacity duration-300 opacity-100',
    exitActive: 'transition-opacity duration-300 opacity-0',
    exitDone: 'opacity-0',
  };

  const onCloseClick = useCallback(() => {
    closeAiModal();
  }, [closeAiModal]);

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (event.target === modalWrapperRef.current) {
        closeAiModal();
      }
    };
    document.addEventListener('click', onDocumentClick);

    return () => {
      document.removeEventListener('click', onDocumentClick);
    };
  }, [closeAiModal]);

  useEffect(() => {
    if (aiModal) {
      setInAnim(true);
    } else {
      setInAnim(false);
    }
  }, [aiModal]);

  if (!aiModal) return null;

  const { title, content, buttons } = aiModal;

  const buttonsComponents = buttons
    ? buttons.map((button, i) => {
        const onClick = button.onClick || closeAiModal;
        const size = button.size ? getButtonSizeFromString(button.size) : ButtonSize.SM;

        return (
          <Button
            key={'button-' + i}
            type={button.main ? ButtonType.PRIMARY : ButtonType.STROKED}
            size={size}
            onClick={onClick}
            themeColors={props.themeColors ?? webnotesThemeColors}
            themeStyle={props.themeStyle ?? webnotesThemeStyle}
            className={classNames('!px-3', button.classes)}
          >
            {button.text}
          </Button>
        );
      })
    : null;

  return (
    <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-50" ref={modalWrapperRef}>
      <CSSTransition nodeRef={modalRef} in={inAnim} timeout={300} classNames={transitionClassNames}>
        <div
          className="rounded-lg bg-white px-6 pt-8 pb-6 opacity-0"
          style={{ boxShadow: '0px 2px 15px 0px #A3A8AD38' }}
          ref={modalRef}
        >
          <div className="flex items-start justify-between">
            <h2 className="mr-2 text-lg font-semibold">{title}</h2>
            <button className="cursor-pointer text-neutral-500 p-1" onClick={onCloseClick}>
              <Icon icon={IconsMap.CLOSE} className="w-4" />
            </button>
          </div>
          {content ? <div className="mt-3">{content}</div> : null}
          {buttonsComponents ? (
            <div className="flex items-center justify-end gap-3 mt-5">{buttonsComponents}</div>
          ) : null}
        </div>
      </CSSTransition>
    </div>
  );
}
